import React from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { PiCheckBold, PiMinusLight, PiPuzzlePieceLight, PiSparkleLight } from "react-icons/pi";
import { LicensePlan } from "../../../store/actions/PlanActions";
import {
  aiFeatureLabel,
  catalogueRows,
  planAiFeatures,
  planSections,
  sectionLabel,
} from "./planCatalogue";

/**
 * Everything each package includes, side by side.
 *
 * The sidebar only shows what the firm has licensed, so this table is where a
 * firm sees what it does NOT have yet and which package (or add-on) brings it.
 * Rows are every section and AI feature any offered package or add-on grants,
 * in the sidebar's groups; a cell is a tick, "Add-on" (not in this package but
 * buyable on top of it - add-ons are only sold on the plan that is
 * add-on eligible), or a dash.
 */
interface PlanComparisonProps {
  plans: LicensePlan[];
  addons: LicensePlan[];
  currentPlanName: string | null;
  /** The one base plan add-ons can be bought on (pons_starter today). */
  addonEligiblePlanName?: string;
}

type Cell = "included" | "addon" | "none";

const PlanComparison: React.FC<PlanComparisonProps> = ({
  plans,
  addons,
  currentPlanName,
  addonEligiblePlanName = "pons_starter",
}) => {
  const { t } = useTranslation();
  if (plans.length === 0) return null;

  const { sections, aiFeatures } = catalogueRows([...plans, ...addons]);
  const addonSections = new Set(addons.flatMap(planSections));
  const addonAi = new Set(addons.flatMap(planAiFeatures));

  const cell = (plan: LicensePlan, key: string, kind: "section" | "ai"): Cell => {
    const has = kind === "section" ? planSections(plan) : planAiFeatures(plan);
    if (has.includes(key)) return "included";
    const viaAddon = kind === "section" ? addonSections.has(key) : addonAi.has(key);
    return viaAddon && plan.name === addonEligiblePlanName ? "addon" : "none";
  };

  const renderCell = (value: Cell) => {
    if (value === "included") {
      return (
        <PiCheckBold
          className="mx-auto h-3.5 w-3.5 text-success-600 dark:text-success-400"
          aria-label={t("Included")}
        />
      );
    }
    if (value === "addon") {
      return (
        <span className="inline-flex items-center gap-1 text-[11px] text-fg-muted">
          <PiPuzzlePieceLight className="h-3 w-3" aria-hidden />
          {t("Add-on")}
        </span>
      );
    }
    return <PiMinusLight className="mx-auto h-3.5 w-3.5 text-fg-muted opacity-50" aria-label={t("Not included")} />;
  };

  const isCurrent = (plan: LicensePlan) => plan.name === currentPlanName;
  const colClass = (plan: LicensePlan) =>
    clsx("px-3 py-2 text-center align-middle", isCurrent(plan) && "bg-champagne/10");

  const groupHeader = (label: React.ReactNode, key: string) => (
    <tr key={key}>
      <th
        scope="colgroup"
        colSpan={plans.length + 1}
        className="px-3 pb-1.5 pt-4 text-start text-xs font-medium text-fg-muted"
      >
        {label}
      </th>
    </tr>
  );

  return (
    <div className="overflow-x-auto custom-scrollbar-main" data-testid="plan-comparison">
      <table className="w-full min-w-[32rem] border-collapse text-xs">
        <thead>
          <tr className="border-b border-hairline-soft">
            <th scope="col" className="px-3 py-2 text-start font-normal text-fg-muted">
              <span className="sr-only">{t("Feature")}</span>
            </th>
            {plans.map((plan) => (
              <th
                key={plan.id}
                scope="col"
                className={clsx(colClass(plan), "font-medium text-fg-strong")}
              >
                <span className="block">{plan.display_name}</span>
                {isCurrent(plan) && (
                  <span className="block text-[10px] font-normal text-fg-muted">{t("Your plan")}</span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sections.map((group) => (
            <React.Fragment key={group.label}>
              {groupHeader(t(group.label), `g-${group.label}`)}
              {group.keys.map((key) => (
                <tr key={key} className="border-b border-hairline-soft/60">
                  <th scope="row" className="px-3 py-2 text-start font-normal text-fg-body">
                    {t(sectionLabel(key))}
                  </th>
                  {plans.map((plan) => (
                    <td key={plan.id} className={colClass(plan)}>
                      {renderCell(cell(plan, key, "section"))}
                    </td>
                  ))}
                </tr>
              ))}
            </React.Fragment>
          ))}
          {aiFeatures.length > 0 && (
            <>
              {groupHeader(
                <span className="inline-flex items-center gap-1">
                  <PiSparkleLight className="h-3 w-3" aria-hidden />
                  {t("AI features")}
                </span>,
                "g-ai"
              )}
              {aiFeatures.map((key) => (
                <tr key={`ai-${key}`} className="border-b border-hairline-soft/60">
                  <th scope="row" className="px-3 py-2 text-start font-normal text-fg-body">
                    {t(aiFeatureLabel(key))}
                  </th>
                  {plans.map((plan) => (
                    <td key={plan.id} className={colClass(plan)}>
                      {renderCell(cell(plan, key, "ai"))}
                    </td>
                  ))}
                </tr>
              ))}
            </>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default PlanComparison;
